import * as React from 'react';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import type { RunsheetDriverJob } from './models/JobScheduleInterfaces';
import { GetQuery, PostQuery } from './Shared';
import { Link } from 'react-router-dom';

interface RunSheetDriverUpdateOffice {
    loading: boolean;
    errors: any;
}

interface RunSheetDriverOfficeProps {
    job: RunsheetDriverJob;
    updateJobField(fieldName: string, fieldValue: string): void;
    updateJobItemFieldNumber(itemIndex: number, fieldName: string, fieldValue: number): void;
    updateJobKsFieldNumber(itemIndex: number, ksItemIndex: number, fieldName: string, fieldValue: number): void;
    save(): void;
}

export default class RunSheetDriverOffice extends React.Component<RunSheetDriverOfficeProps, RunSheetDriverUpdateOffice> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: false,
            errors: {}
        };
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["times"];
            if (e.target.name === "planChangesReason") {
                delete errors["reason"];
            }
            this.setState({ errors: errors });
        }

        this.props.updateJobField(e.target.name, e.target.value);
    }

    onCellChange = (e: any, itemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        var value = e.target.value * 1;
        this.props.updateJobItemFieldNumber(itemIndex, e.target.name, value);
    }

    onCellQtyTotalChange = (e: any, itemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        var qtyTotal = e.target.value * 1;
        this.calcPrice(itemIndex, qtyTotal);
    }

    calcPrice = async (itemIndex: number, quantity: number) => {
        //update item that has changed
        var job = this.props.job;
        var item = job.items[itemIndex];
        this.props.updateJobItemFieldNumber(itemIndex, "quantityTotal", quantity * 1);

        const apiUrl = 'CalculatePlanChangesPrice?JobId=' + job.jobId + '&ScheduleId=' + item.id + '&Quantity=' + quantity;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            if (result.data.amount > 0) {
                //only update the quote prices if there is a pricelist amount
                this.props.updateJobItemFieldNumber(itemIndex, "price", result.data.amount);
                this.props.updateJobItemFieldNumber(itemIndex, "overhireRate", result.data.overhireRate);
            }
        }
    }

    onKsCellChange = (e: any, itemIndex: number, ksItemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        var value = e.target.value * 1;
        this.props.updateJobKsFieldNumber(itemIndex, ksItemIndex, e.target.name, value);
    }

    onKsCellQtyTotalChange = (e: any, itemIndex: number, ksItemIndex: number) => {
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors["items"];
            this.setState({ errors: errors });
        }

        var qtyTotal = e.target.value * 1;
        this.calcKsPrice(itemIndex, ksItemIndex, qtyTotal);
    }

    async calcKsPrice(itemIndex: number, ksItemIndex: number, quantity: number) {
        //update item that has changed
        var job = this.props.job;
        var items = [...job.items]; 
        var item = items[itemIndex];
        var ksItems = [...item.kwikstageItems];
        var ksItem = ksItems[ksItemIndex];

        this.props.updateJobKsFieldNumber(itemIndex, ksItemIndex, "baysTotal", quantity);

        var calcKsPrice = {
            scheduleId: item.id,
            scheduleKwikstageId: ksItem.scheduleKwikstageId,
            ksItems: ksItems
        };

        var jsonItem = JSON.stringify(calcKsPrice);

        const apiUrl = "CalculatePlanChangesKwikstagePrice";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.amount > 0) {
                //only update the quote prices if there is a pricelist amount
                this.props.updateJobItemFieldNumber(itemIndex, "price", result.data.amount);
                this.props.updateJobItemFieldNumber(itemIndex, "overhireRate", result.data.overhireRate);
            }
        }
    }

    validate = () => {
        let errors: any = {};
        let formIsValid = true;
        var errorStringTimes = "";
        var error = false;

        let job = this.props.job;

        //Check Times
        if (job.departureTime == "") {
            errorStringTimes += "Departure Time, ";
            formIsValid = false;
        }
        if (job.siteArrivalTime == "") {
            errorStringTimes += "Site Arrival Time, ";
            formIsValid = false;
        }
        if (job.downTime && !DateHelpers.validTimeAmount(job.downTime)) {
            errorStringTimes += "Valid Down Time, ";
            formIsValid = false;
        }
        if (job.siteCompletionTime == "") {
            errorStringTimes += "Site Completion Time, ";
            formIsValid = false;
        }

        if (job.departureTime != "" && job.siteArrivalTime != "") {
            if (DateHelpers.validTimeAmount(job.siteArrivalTime) && DateHelpers.validTimeAmount(job.departureTime)) {
                if (!DateHelpers.compareTimeGreater(job.departureTime, job.siteArrivalTime)) {
                    errorStringTimes += "Arrival Time needs to be > than Departure Time \n";
                }
            } else {
                errorStringTimes += "Departure Time and Arrival Time need to be valid times \n";
            }
        }

        if (job.siteCompletionTime != "" && job.siteArrivalTime != "") {
            if (DateHelpers.validTimeAmount(job.siteArrivalTime) && DateHelpers.validTimeAmount(job.siteCompletionTime)) {
                if (!DateHelpers.compareTimeGreater(job.siteArrivalTime, job.siteCompletionTime)) {
                    errorStringTimes += "Completion Time needs to be > than Arrival Time \n";
                }
            } else {
                errorStringTimes += "Arrival Time and Completion Time need to be valid times \n";
            }
        }

        if (errorStringTimes != "") {
            formIsValid = false;
            errors["times"] = "Please enter the following times: " + errorStringTimes.substring(0, errorStringTimes.length - 2);
        }

        if (job.officeNotes === "") {
            formIsValid = false;
            errors["reason"] = "Reason for change is required";
        }

        //Check Quantity and Price
        var errorString = "Please enter the following values: ";
        var rowNumber = 0;
        let items = job.items;

        for (var i = 0; i < items.length; i++) {
            let fields = items[i];
            rowNumber += 1;
            var newErrorString = "";

            //Check Non-Kwikstage Items first
            if (!fields.isKwikstage) {
                //check normal quantity
                let quantityTotal = fields.quantityTotal * 1;
                if (quantityTotal < 1) {
                    error = true;
                    newErrorString += "Quantity Total, ";
                }

                let quantity = fields.quantity * 1;
                if (quantity < 1) {
                    error = true;
                    newErrorString += "Quantity, ";
                }

                if (quantity > quantityTotal) {
                    error = true;
                    newErrorString += "Quantity must be <= Quantity Total, ";
                }
            }

            //Check Prices
            if (fields.price < 0.01) {
                error = true;
                newErrorString += "Price, ";
            }

            if (fields.overhireRate < 0.01) {
                error = true;
                newErrorString += "Overhire Rate, ";
            }
            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
            }

            //Check Kwikstage Items (as these are on the next row/s)
            if (fields.isKwikstage) {
                //check kwikstage quantities
                for (var k = 0; k < fields.kwikstageItems.length; k++) {
                    rowNumber += 1;
                    var newErrorString = "";
                    var field = fields.kwikstageItems[k];

                    let baysTotal = field.baysTotal * 1;
                    if (baysTotal < 1) {
                        newErrorString += "Bays Total, ";
                        error = true;
                    }

                    let baysComplete = field.baysComplete * 1;
                    if (baysComplete < 1) {
                        newErrorString += "Bays Complete, ";
                        error = true;
                    }

                    if (baysComplete > baysTotal) {
                        newErrorString += "Bays Complete must be <= Bays Total, ";
                        error = true;
                    }

                    if (newErrorString != "") {
                        errorString += "\n"
                        errorString += "Row " + rowNumber + ": " + newErrorString;
                    }
                }
            }
        }
        if (error) {
            formIsValid = false;
            errorString = errorString.substring(0, errorString.length - 2);
            errors["items"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    save = () => {
        if (this.validate()) {
            this.props.save();
        } else {
            toast.error("Please fix the validation errors on screen before saving!");
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        let job = this.props.job;

        return (
            <div>
                 <div>
                    <h5>
                        <span>{job.truck}</span>
                        <Link className={job.jobId === 0 ? "hidden" : "marginLeft20"} to={'/job/' + job.jobId + '/details'} target="_blank">
                            {job.jobId}
                        </Link>
                        <span className="marginLeft20">{job.siteAddress}</span>
                    </h5>
                </div>
                <div>
                    {job.driverNotes}
                </div>
                <div>
                    <label className="input-group--inline" htmlFor="estimatedTime">
                        <span className="label-small">Est. Time:</span>
                        <span className="marginLeft10" id="estimatedTime">{job.estimatedTime}</span>
                    </label>
                    <label className="input-group--inline" htmlFor="departureTime">
                        <span className="label-small">Departure:</span>
                        <input className="marginLeft10" id="departureTime" name="departureTime" type="time" value={job.departureTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <label className="input-group--inline" htmlFor="siteArrivalTime">
                        <span className="label-small">Arrival:</span>
                        <input className="marginLeft10" id="siteArrivalTime" name="siteArrivalTime" type="time" value={job.siteArrivalTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <label className="input-group--inline" htmlFor="downTime">
                        <span className="label-small">Down Time:</span>
                        <input className="form-control runsheet__timeSize marginLeft10" type='text' id="downTime" name="downTime" pattern="(0?[0-9]|1[0-2]):[0-5][0-9]$" value={job.downTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <label className="input-group--inline" htmlFor="siteCompletionTime">
                        <span className="label-small">Completion:</span>
                        <input className="marginLeft10" id="siteCompletionTime" name="siteCompletionTime" type="time" value={job.siteCompletionTime} onChange={(e) => this.handleChange(e)} />
                    </label>
                </div>
                <div className={this.state.errors["times"] ? "" : "hidden"}><span className="label errors">{this.state.errors["times"]}</span></div>
                <div>
                    <table className='runsheetDriverTable runsheetDriverTable--width table tableColours-header'>
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th>Unit</th>
                                <th>Item</th>
                                <th className="textalignright">Qty Total</th>
                                <th className="textalignright">Qty</th>
                                <th className="textalignright">Price</th>
                                <th className="textalignright">Overhire Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {job.items.map((item, itemIndex) =>
                                <React.Fragment key={itemIndex}>
                                    <tr className="runsheetRow">
                                        <td className="hidden">{item.id}</td>
                                        <td className="table__text--align">{item.unitNumber === "" ? "" : "Unit " + item.unitNumber}</td>
                                        <td className="table__text--align table--col-l">{item.item}</td>
                                        <td className="table__text--align table--col-m">
                                            <input type='number' min="0" step="1" className={item.isKwikstage ? "hidden" : "form-control textalignright"} id="quantityTotal" name="quantityTotal" value={item.quantityTotal} onChange={(e) => this.onCellQtyTotalChange(e, itemIndex)} disabled />
                                        </td>
                                        <td className="table__text--align table--col-m">
                                            <input type='number' min="0" step="1" className={item.isKwikstage ? "hidden" : "form-control textalignright"} id="quantity" name="quantity" value={item.quantity} onChange={(e) => this.onCellChange(e, itemIndex)} />
                                        </td>
                                        <td className="table__text--align table--col-m">
                                            <input type='number' min="0" step="0.01" className="form-control textalignright" id="price" name="price" value={item.price} onChange={(e) => this.onCellChange(e, itemIndex)} disabled />
                                        </td>
                                        <td className="table__text--align table--col-m">
                                            <input type="number" min="0" step="0.01" className="form-control textalignright" id="overhireRate" name="overhireRate" value={item.overhireRate} onChange={(e) => this.onCellChange(e, itemIndex)} disabled />
                                        </td>
                                    </tr >
                                    <tr className={item.kwikstageItems.length > 0 ? "runsheetRow" : "hidden"}>
                                        <td colSpan={6}>
                                            <table>
                                                <tbody>
                                                    {item.kwikstageItems.map((ksItem, ksIndex) =>
                                                        <tr key={itemIndex} className={item.typeId === 1 ? "tableColours--install" : item.typeId === 2 ? "tableColours--pickup" : item.typeId === 3 ? "tableColours--assist" : "tableColours--cleaning"}>
                                                            <td className="hidden">{ksItem.scheduleKwikstageId}</td>
                                                            <td className="table__text--align">{ksItem.starterBay} - {ksItem.lift}</td>
                                                            <td className="table__text--align table--col-m">
                                                                <input type='number' min="0" step="1" className="form-control textalignright" id="baysTotal" name="baysTotal" value={ksItem.baysTotal} onChange={(e) => this.onKsCellQtyTotalChange(e, itemIndex, ksIndex)} />
                                                            </td>
                                                            <td className="table__text--align table--col-m">
                                                                <input type='number' min="0" step="1" className="form-control textalignright" id="baysComplete" name="baysComplete" value={ksItem.baysComplete} onChange={(e) => this.onKsCellChange(e, itemIndex, ksIndex)} />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            )}
                        </tbody>

                    </table>
                    {this.state.errors["items"] ?
                        (this.state.errors["items"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
                <div>
                    <label className="input-group--nowrap input-group--nowrap__max--width" htmlFor="officeNotes">
                        <span className="label">Reason for change</span>
                        <textarea className="input runsheetDriver-reason--size" id="officeNotes" name="officeNotes" value={job.officeNotes} onChange={(e) => this.handleChange(e)}></textarea>
                    </label>
                    <span className="label errors">{this.state.errors["reason"]}</span>
                </div>
                <button className="defaultbutton" type="button" onClick={this.save}>Save</button>
            </div>


        );

    }

};

